import React from 'react';
import { Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { SideMenuModal } from './SideMenuModal';
import { colors } from '../constants/colors';
import { useVideoPreviewControls } from './VideoPreview';

interface ControlsModalProps {
  onClose: () => void;
  activeAuction: any;
  onEndShow: () => void;
  isBroadcasting: boolean;
  onClaimBroadcast: () => void;
}

const sectionLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: colors.gray[600],
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 8,
};

export function ControlsModal({
  onClose,
  activeAuction,
  onEndShow,
  isBroadcasting,
  onClaimBroadcast,
}: ControlsModalProps) {
  const { isAudioEnabled, isVideoEnabled, toggleAudio, toggleVideo } = useVideoPreviewControls();

  const toggleBtnBase: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '10px 12px',
    borderRadius: 10,
    border: 'none',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const onStyle: React.CSSProperties = {
    ...toggleBtnBase,
    background: colors.gray[100],
    color: colors.ink.black,
  };

  const offStyle: React.CSSProperties = {
    ...toggleBtnBase,
    background: `${colors.accent.pink}15`,
    color: colors.accent.pink,
  };

  return (
    <SideMenuModal title="Show Controls" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Audio & Video — only show when this device is broadcasting */}
        {isBroadcasting && (
          <div>
            <div style={sectionLabel}>AUDIO &amp; VIDEO</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={toggleAudio} style={isAudioEnabled ? onStyle : offStyle}>
                {isAudioEnabled ? <Mic size={14} /> : <MicOff size={14} />}
                {isAudioEnabled ? 'Mic On' : 'Mic Off'}
              </button>
              <button onClick={toggleVideo} style={isVideoEnabled ? onStyle : offStyle}>
                {isVideoEnabled ? <Video size={14} /> : <VideoOff size={14} />}
                {isVideoEnabled ? 'Cam On' : 'Cam Off'}
              </button>
            </div>
          </div>
        )}

        {/* Broadcast */}
        {!isBroadcasting && (
          <div>
            <div style={sectionLabel}>BROADCAST</div>
            <button
              onClick={onClaimBroadcast}
              style={{
                width: '100%',
                padding: 10,
                background: colors.gray[100],
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                color: colors.brand.blue,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Broadcast from this device
            </button>
          </div>
        )}

        {/* End Show */}
        <div>
          <div style={sectionLabel}>END SHOW</div>
          <button
            onClick={onEndShow}
            disabled={!!activeAuction}
            style={{
              width: '100%',
              padding: 12,
              background: colors.accent.pink,
              color: colors.white,
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              border: 'none',
              cursor: activeAuction ? 'not-allowed' : 'pointer',
              opacity: activeAuction ? 0.5 : 1,
            }}
          >
            End Show
          </button>
          {activeAuction && (
            <div style={{ fontSize: 12, color: colors.gray[600], marginTop: 6 }}>
              Wait for the active auction to finish first.
            </div>
          )}
        </div>
      </div>
    </SideMenuModal>
  );
}
